/**
 * Big data API types
 */

import { PaginatedResponse, PaginationQueryParams } from './pagination';

type UtilRequiredKeys<T, K extends keyof T> = Omit<T, K> & Required<Pick<T, K>>;

/**
 * Indicates whether vehicle is
 *   * hotlist: Vehicle flagged for tracking
 *   * exempted: Vehicle is exempt from watchlist rule and other rules. This is useful, for instance, when an organization wishes to avoid alerts on known vehicles, like employee vehicles.
 *
 *   * none: Empty value provided when no special consideration for the vehicle
 * @example "exempted"
 */
export enum LPRSecurityStatus {
  Hotlist = 'hotlist',
  Exempted = 'exempted',
}

/**
 * Enumerations of standard colors for vehicles.
 * @example "yellow"
 */
export enum LPRColorDefinitions {
  Black = 'black',
  Blue = 'blue',
  Brown = 'brown',
  GoldBeige = 'gold-beige',
  Green = 'green',
  Gray = 'gray',
  Silver = 'silver',
  SilverGray = 'silver-gray',
  Orange = 'orange',
  Pink = 'pink',
  Purple = 'purple',
  Red = 'red',
  White = 'white',
  Yellow = 'yellow',
}

/**
 * License plate number of the vehicle in uppercase
 * @example "ABC123"
 */
export type LPRPlate = string;

/** Encompasses all the fields of the vehicle record that are editable */
export interface LPRVehicleListUntaggedRecordUpdate {
  /**
   * Date from which this record is valid
   * @format date
   * @example "2022-01-24T00:00:00.000Z"
   */
  validFrom?: string;
  /**
   * Date upto which this record is valid
   * @format date
   * @example "2022-02-25T00:00:00.000Z"
   */
  validTo?: string;
  /** It signifies a week long user schedule. This schedule is effective according to user timezone. */
  schedule?: LPRSchedule;
  accessType?: LPRAccessType;
  /**
   * Indicates whether vehicle is
   *   * hotlist: Vehicle flagged for tracking
   *   * exempted: Vehicle is exempt from watchlist rule and other rules. This is useful, for instance, when an organization wishes to avoid alerts on known vehicles, like employee vehicles.
   *
   *   * none: Empty value provided when no special consideration for the vehicle
   */
  securityStatus?: LPRSecurityStatus;
  /**
   * Object containing all user information associated with the specific event. For example, for a plate read, this would contain user supplied attributes for the given plate like apartment number. the following are the guidelines for supplying User Data:
   *   * Fields Should be in camelCase
   *   * Maximum of 5 unique keys.
   *   * Each key or value should have only 256 characters max.
   */
  userData?: LPRUserData;
}

/**
 * Object containing all user information associated with the specific event. For example, for a plate read, this would contain user supplied attributes for the given plate like apartment number. the following are the guidelines for supplying User Data:
 *   * Fields Should be in camelCase
 *   * Maximum of 5 unique keys.
 *   * Each key or value should have only 256 characters max.
 * @example {"type":"een.userData.v1","apartmentNum":"105A"}
 */
export type LPRUserData = object;

export interface LPREntityDescriptor {
  /** Value to be used for presenting in UI */
  display?: string;
  /** Value to be used for backend processing */
  value?: string;
}

/** Interval of a schedule */
export interface LPRInterval {
  /**
   * Starting point of the interval. It should be in the range of 00:00 to 23:59 since this is the range for a day
   * @pattern ^(0[0-9]|1[0-9]|2[0-3]):[0-5][0-9]$
   */
  start: string;
  /**
   * End point of the interval. It should be in the range of 00:00 to 23:59 since this is the range for the day
   * @pattern ^(0[0-9]|1[0-9]|2[0-3]):[0-5][0-9]$
   */
  end: string;
}

/** It signifies a week long user schedule. This schedule is effective according to user timezone. */
export interface LPRSchedule {
  sunday?: LPRInterval[];
  monday?: LPRInterval[];
  tuesday?: LPRInterval[];
  wednesday?: LPRInterval[];
  thursday?: LPRInterval[];
  friday?: LPRInterval[];
  saturday?: LPRInterval[];
}

/** User supplied attributes of the list, that does not return the individual vehicle records, to be used during patching of list */
export type LPRVehicleListMetaUntaggedForPatch = LPRVehicleListMetaUntagged;

export type LPREventV1 = any;

/**
 * Denotes the type of actor
 * @default "camera"
 */

export enum LPRActorType {
  Camera = 'camera',
  Bridge = 'bridge',
  User = 'user',
  Account = 'account',
  Location = 'location',
}

/** Specifies the attributes of an actor, useful for passing as a filter */
export interface LPRActorFilter {
  /**
   * The id of the thing that caused the event This can be camera or location ID. If not supplied for any entry in the array, it is taken to mean as the entire account.
   * @example "100b913c"
   */
  id?: string;
  /**
   * Type of actor that caused the event.
   * @default "camera"
   */
  type: 'camera' | 'location';
}

/** Specifies the complete attributes of an actor, including the user-supplied field `name` */
export type LPRActor = LPRActorFilter & {
  /**
   * User-defined name for the actor
   * @example "Entrance LPR Camera"
   */
  name?: string;
};

export enum LPRAccessType {
  Allow = 'allow',
  Deny = 'deny',
}

/** Specifies the attributes of an actor, useful for passing as a filter */
export interface LPRActorFilter {
  /**
   * The id of the thing that caused the event This can be camera or location ID. If not supplied for any entry in the array, it is taken to mean as the entire account.
   * @example "100b913c"
   */
  id?: string;
  /**
   * Type of actor that caused the event.
   * @default "camera"
   */
  type: 'camera' | 'location';
}

/** List of actors against which filtering to happen (camera/location/account) */
export interface LPRResourceFilter {
  /** List of the actors for which events are checked. */
  actors?: LPRActorFilter[];
}

/** User supplied attributes of the list, that does not return the individual vehicle records, to be used during creation of list or patching of list */
export interface LPRVehicleListMetaUntagged {
  /**
   * User supplied name for the list
   * @example "EmployeesList"
   */
  name?: string;
  /**
   * Whether list is enabled
   * @default false
   */
  enabled?: boolean;
  /**
   * A verbose explanation of the vehicle list
   * @example "This is vehicle list of denied vehicles"
   */
  notes?: string;
  /** List of actors against which filtering to happen (camera/location/account) */
  resourceFilter?: LPRResourceFilter;
}

/** User supplied and System generated attributes of the list. However it excludes the vehicle records */
export type LPRVehicleListMeta = LPRVehicleListMetaUntagged & {
  /**
   * Unique system generated id for list
   * @example "ABC123-20221014123567"
   */
  id: string;
  /**
   * Timestamp when this list created
   * @format date-time
   * @example "2017-02-27T10:44:13.423Z"
   */
  createTimestamp?: string;
  /**
   * Timestamp when this list last updated
   * @format date-time
   * @example "2017-02-27T10:44:13.423Z"
   */
  updateTimestamp?: string;
  /**
   * User id of the creator of the vehicle list
   * @example "ca0de584"
   */
  createdByUserId?: string;
};

/** User supplied attributes of the list, that does not return the individual vehicle records, to be used during creation of list */
export type LPRVehicleListMetaUntaggedForPost = UtilRequiredKeys<LPRVehicleListMetaUntagged, 'name'>;

/** Encompasses all the fields of the vehicle record that are editable */
export interface LPRVehicleListUntaggedRecordUpdate {
  /**
   * Date from which this record is valid
   * @format date
   * @example "2022-01-24T00:00:00.000Z"
   */
  validFrom?: string;
  /**
   * Date upto which this record is valid
   * @format date
   * @example "2022-02-25T00:00:00.000Z"
   */
  validTo?: string;
  /** It signifies a week long user schedule. This schedule is effective according to user timezone. */
  schedule?: LPRSchedule;
  accessType?: LPRAccessType;
  /**
   * Indicates whether vehicle is
   *   * hotlist: Vehicle flagged for tracking
   *   * exempted: Vehicle is exempt from watchlist rule and other rules. This is useful, for instance, when an organization wishes to avoid alerts on known vehicles, like employee vehicles.
   *
   *   * none: Empty value provided when no special consideration for the vehicle
   */
  securityStatus?: LPRSecurityStatus;
  /**
   * Object containing all user information associated with the specific event. For example, for a plate read, this would contain user supplied attributes for the given plate like apartment number. the following are the guidelines for supplying User Data:
   *   * Fields Should be in camelCase
   *   * Maximum of 5 unique keys.
   *   * Each key or value should have only 256 characters max.
   */
  userData?: LPRUserData;
}

/** Describes each record of the list before addition to the vehicle List, in other words, all the user supplied attributes of a specific vehicle. This is a union of the editable fields and create-only user-supplied fields. */
export type LPRVehicleListUntaggedRecord = LPRVehicleListUntaggedRecordUpdate & {
  /** License plate number of the vehicle in uppercase */
  plate: LPRPlate;
};

/** Union of user attributes of a given vehicle and system generated attributes (unique id) */
export type LPRVehicleListTaggedRecord = LPRVehicleListUntaggedRecord & {
  /**
   * Unique system generated id for record
   * @example "ABC123-20221014123567"
   */
  id: string;
  /**
   * Timestamp when this record created
   * @format date-time
   * @example "2017-02-27T10:44:13.423Z"
   */
  createTimestamp: string;
  /**
   * Timestamp when this record last updated
   * @format date-time
   * @example "2017-02-27T10:44:13.423Z"
   */
  updateTimestamp?: string;
};

/** Union of user attributes of a given vehicle and system generated attributes (unique id), along with its association to the appropriate Vehicle List */
export type LPRVehicleListPaddedRecord = LPRVehicleListTaggedRecord & {
  /**
   * Id of the list this record belongs to
   * @format uuid
   */
  listId: string;
};

/** LprSummaryAggregate */
export interface LPRSummaryAggregate {
  /**
   * ESN of the camera to which the following event counts belong to or `all` for the aggregate summary
   * @example "100b913c"
   */
  actorId?: string;
  /** Count of events in last hour */
  lastHour?: number;
  /** Count of events for the current day of the user (in user's local time) */
  today?: number;
  /** Count of events for the previous day of hte user (in user's local time) */
  yesterday?: number;
  /** Count of events for the previous 30 days. This is independent of local time. */
  last30Days?: number;
  /** Count of events for previous 7 days. This is independent of local time. */
  last7Days?: number;
}

/** LprFieldsData */
export interface LPRFieldsData {
  /** List of all actor Ids that have contributed to lpr events, typically refers to camera ESNs, locations */
  actor: LPRActor[];
  /** @example ["blue","black"] */
  color: LPRColorDefinitions[];
  /**
   * List of the distinct vehicle makes present across all the events for this account
   * @example ["ford","toyota"]
   */
  make: string[];
  /**
   * List of the distinct vehicle models present across all events for this account
   * @example ["camry","fusion"]
   */
  model: string[];
  /**
   * List of the distinct provinces/states present across all events for this account. List values in accordance with ISO 3166.2
   * @example ["US-NY","US-TX"]
   */
  plateRegion: string[];
  /**
   * List of the distinct directions present across all events for this account.
   * @example ["entry","exit"]
   */
  direction: ('entry' | 'exit')[];
  /**
   * List of the access types present across all events for this account.
   * @example ["allow","deny"]
   */
  accessType: LPRAccessType[];
  /**
   * List of the distinct vehicle body types present across all the events for this account
   * @example ["sedan","jeep"]
   */
  bodyType: string[];
}

/**
 * Enumerates available rule types. The brief synopsis of the rules as follows.
 *  * `lprRuleAccessControlAllow` and `lprRuleAccessControlDeny` create an alert when vehicles are seen whose access type is `allow` or `deny` respectively.
 *  * `lprRuleWatch` sets up a timer on those vehicles for all vehicles except where `securityStatus` is set to `exempted`. If these vehicles are seen once and not seen again within the specified time interval, an alert is raised.
 *  * `lprRuleCountAggr` sets up aggregation for camera or location and fires an alert if the aggregation
 *     (detection count) in a specified time interval falls above or below threshold.
 */
export enum LPRRuleTypes {
  LprRuleAccessControlAllow = 'lprRuleAccessControlAllow',
  LprRuleAccessControlDeny = 'lprRuleAccessControlDeny',
  LprRuleWatch = 'lprRuleWatch',
  LprRuleCountAggr = 'lprRuleCountAggr',
  LprRuleHotlist = 'lprRuleHotlist',
  LprRuleUnlisted = 'lprRuleUnlisted',
}

export type LPRRuleType = LPRRuleTypes;

/**
 * Defines the priority of the alert
 * @format int32
 * @min 1
 * @max 10
 */
export type LPRPriority = number;

/** A list of attributes of the event used to funnel down a specific set of events for any downstream action. This filtering construct is as such independent of VSP and quite generic */
export interface LPREventFilter {
  /** List of event types allowed to be processed by this rule */
  eventTypes?: string[];
  /** It signifies a week long user schedule. This schedule is effective according to user timezone. */
  schedule?: LPRSchedule;
  /** List of actors against which filtering to happen (camera/location/account) */
  resourceFilter?: LPRResourceFilter;
}

/** A flag that indicates whether to open boom barrier / vending gate, if any associated. This rule property may be interpreted by any LPR algorithm that runs for the cameras/locations/account to which this rule applies. */
export interface LPRBarrierOpen {
  barrierOpen: boolean;
}

export interface LPRRuleMapList {
  /**
   * Enumerates the IDs of all Vehicle Lists that are under the purview of this rule.
   * @maxItems 1
   */
  vehicleListIds: string[];
}

export interface LPRRuleMapPlate {
  /**
   * Specific license plates on which the rule fires. This can be used to map specific plates to a rule, without the need to add them to a vehicle list.
   * @maxItems 1
   */
  plates: string[];
}

export type LPRRuleToVehFilter = LPRRuleMapPlate | LPRRuleMapList;

/** Configuration Parameters pertaining to Event Aggregation Rule. This rule checks for the count vehicle events in a window of specified size, for any camera under the purview of this rule. It fires on this count being either above or below this threshold, as specified by the configuration parameters. In other words, the rule can be used for checking either too many or too few events in a given time window. */
export interface LPRRuleCFGEventAggr {
  /**
   * Window size of event aggregation. For example, if set to 1h, then the rule is applied on every sliding window of duration 1 hour.
   * @example "1h"
   */
  windowSize?: string;
  /**
   * Count of vehicle threshold for alert
   * @format int32
   */
  countThr?: number;
  /** If true, alert fires on count > threshold, else it fires on below thr */
  greaterThanThr?: boolean;
}

/**
 * Configuration parameters for access control Rule Behavior
 * @example {"barrierOpen":true,"plates":["ABC123"]}
 */
export type LPRRuleCFGAccessControl = LPRBarrierOpen & LPRRuleToVehFilter;

export interface LPRDurationThresh {
  /**
   * Time threshold within which same vehicle (same license plate) needs to be seen again, failing which, an alert is fired.
   * @example "1h"
   */
  durationThresh?: string;
}

/** Configuration parameters for the Vehicle Watch or Overstay rule */
export type LPRRuleCFGWatch = LPRDurationThresh & LPRRuleToVehFilter;

/** Encompasses all the user specified properties of a rule used to generate alerts. */
export interface LPRAlertConditionRuleBase {
  type?: LPRRuleType;
  /**
   * Meaningful name for the rule
   * @example "Allow Employees vehicles"
   */
  name?: string;
  /** Defines the priority of the alert */
  priority?: LPRPriority;
  /**
   * A verbose explanation of the rule
   * @example "This rule filters plates belonging to global employee list and allows access"
   */
  notes?: string;
  /**
   * Whether rule is enabled
   * @default false
   */
  enabled?: boolean;
  /** A list of attributes of the event used to funnel down a specific set of events for any downstream action. This filtering construct is as such independent of VSP and quite generic */
  eventFilter?: LPREventFilter;
  configuration?: object;
}

/**
 * Configuration parameters for access control Rule Behavior
 * @example {"barrierOpen":true,"filterType":"plate","plates":["ABC123"]}
 */
export interface LPRRuleCFGAccessControlBase {
  /**
   * A flag that indicates whether to open boom barrier / vending gate, if any associated. This rule property may be interpreted by any LPR algorithm that runs for the cameras/locations/account to which this rule applies.
   * @example true
   */
  barrierOpen: boolean;
  /**
   * Enumerates available filter types. The brief synopsis of the filters is as follows:
   *   * plate: rule is applied to one or more plate(s)
   *   * vehicleList: rule is applied to one or more vehicle list(s)
   * @example "plate"
   */
  filterType: 'plate' | 'vehicleList';
}

export type LPRRuleCFGAccessControlByPlate = LPRRuleCFGAccessControlBase & LPRRuleMapPlate;

export type LPRRuleCFGAccessControlByList = LPRRuleCFGAccessControlBase & LPRRuleMapList;

export type LPRAlertConditionRuleAccessControl = LPRAlertConditionRuleBase & {
  type?: 'lprRuleAccessControlAllow' | 'lprRuleAccessControlDeny';
  configuration?: LPRRuleCFGAccessControlByPlate | LPRRuleCFGAccessControlByList;
};

export type LPRAlertConditionRuleEventAggr = LPRAlertConditionRuleBase & {
  type?: 'lprRuleCountAggr';
  /** Configuration Parameters pertaining to Event Aggregation Rule. This rule checks for the count vehicle events in a window of specified size, for any camera under the purview of this rule. It fires on this count being either above or below this threshold, as specified by the configuration parameters. In other words, the rule can be used for checking either too many or too few events in a given time window. */
  configuration?: {
    /**
     * Window size of event aggregation. For example, if set to 1h, then the rule is applied on every sliding window of duration 1 hour.
     * @example "1h"
     */
    windowSize: string;
    /**
     * Count of vehicle threshold for alert
     * @format int32
     */
    countThr: number;
    /** If true, alert fires on count > threshold, else it fires on below thr */
    greaterThanThr: boolean;
  };
};

/** Configuration parameters for the Vehicle Watch rule */
export interface LPRRuleCFGWatchBase {
  /**
   * Time threshold within which same vehicle (same license plate) needs to be seen again, failing which, an alert is fired.
   * @example "1h"
   */
  durationThresh?: string;
  originActors?: {
    /**
     * The id of the thing that caused the event This can be camera or location ID.
     * @example "100b913c"
     */
    id?: string;
    /**
     * Type of actor that caused the event.
     * @default "camera"
     */
    type: 'camera' | 'location';
  }[];
  destinationActors?: {
    /**
     * The id of the thing that caused the event This can be camera or location ID.
     * @example "100b913c"
     */
    id?: string;
    /**
     * Type of actor that caused the event.
     * @default "camera"
     */
    type: 'camera' | 'location';
  }[];
  /**
   * Enumerates available filter types. The brief synopsis of the maps as follows:
   *   * plate: rule should be applied to one or more plate(s)
   *   * vehicleList: rule to be applied to one or more vehicle list(s)
   *   * anyPlateExcept: rule to be applied to all plates, except ones which are exempted in the optionally provided vehicle list
   * @example "plate"
   */
  filterType?: 'plate' | 'vehicleList' | 'anyPlateExcept';
}

export interface LPRRuleMapAnyPlateExcept {
  filterType?: 'anyPlateExcept';
  /**
   * Enumerates the IDs of all Vehicle Lists that are used for exemption of vehicles from the rule.
   * @maxItems 1
   */
  exemptionVehicleListIds: string[];
}

export type LPRRuleCFGWatchByPlate = LPRRuleCFGWatchBase & LPRRuleMapPlate;

export type LPRRuleCFGWatchByList = LPRRuleCFGWatchBase & LPRRuleMapList;

export type LPRRuleCFGWatchByAnyPlateExcept = LPRRuleCFGWatchBase & LPRRuleMapAnyPlateExcept;

export type LPRAlertConditionRuleWatch = LPRAlertConditionRuleBase & {
  type?: 'lprRuleWatch';
  configuration?: LPRRuleCFGWatchByPlate | LPRRuleCFGWatchByList | LPRRuleCFGWatchByAnyPlateExcept;
};

/** Encompasses all the user specified properties of a rule used to generate alerts. */
export type LPRAlertConditionRuleUntagged =
  | LPRAlertConditionRuleAccessControl
  | LPRAlertConditionRuleEventAggr
  | LPRAlertConditionRuleWatch;

/** Encompasses all the user specified properties of a rule used to generate alerts. */
export type LPRAlertConditionRuleUntaggedForPost = object & LPRAlertConditionRuleUntagged;

/** Encompasses all the user specified and system generated properties of a rule used to generate alerts. */
export type LPRAlertConditionRule = LPRAlertConditionRuleUntagged & {
  /** The system generated id of this rule resource */
  id?: string;
  /**
   * Time at which the Alert Condition Rule was created.
   * @format date-time
   * @example "2021-04-22T00:00:00.000+00:00"
   */
  createTimeStamp?: string;
  /**
   * Time at which the Alert Condition Rule was updated.
   * @format date-time
   * @example "2021-04-22T00:20:00.000+00:00"
   */
  updateTimeStamp?: string;
  /**
   * User id of the creator of the rule
   * @example "ca0de584"
   */
  createdByUserId?: string;
  /** Possible Alert Types that are producible by this rule. For a given event, what alert is produced depends on the configuration parameters of the rule and the event properties */
  outputAlertTypes?: string[];
};

export interface LPRRulesFields {
  actor?: LPRActor[];
  enabled?: boolean[];
  priority?: LPRPriority[];
  type?: LPRRuleType[];
}

export type LPRVehicleListResult = {
  /** IDs of all the vehicle Lists that satisfy the query. */
  results?: LPRVehicleListMeta[];
};

export type LPRVehicleListEntriesResult = {
  results?: LPRVehicleListPaddedRecord[];
};

export type LPRVehicleListsSearchResult = {
  results?: LPRVehicleListPaddedRecord[];
};

/** Encompasses all the user specified properties of a rule used to generate alerts. */
export type LPRAlertConditionRuleUntaggedForPatch = LPRAlertConditionRuleUntagged;

export type LPRSearchResult = {
  results?: LPREventV1[];
};

export type LPRRulesResult = {
  results?: LPRAlertConditionRule[];
};

export type LPRSummaryData = {
  results?: LPRSummaryAggregate[];
};

/**
 * LPR Vehicle lists
 */

// GET: /lprVehicleLists

export type LPRVehicleListsQueryParams = {
  /**
   * Filter to get only events where the actorType and actorId value equals any one of the supplied value in the list. For each entry of list, the actor type has to be prefixed along with actor id like `actorType:actorId`. For example, to filter for camera with id 100d4c41, the actorId that has to be used is `camera:100d4c41`. To search for events from a specific type of actor, for example users, use a wildcard as actorId: `user:*`.
   * @example ["camera:100d4c41","location:abcd1234"]
   */
  actor?: string[] | string;
  /** Phrase that is used to search for resources (rules or lists) whose names contain it */
  name__contains?: string;
  /** Provide the license plate in uppercase to be searched with an exact match */
  plate?: string;
} & PaginationQueryParams;

export type LPRVehicleListsResponse = PaginatedResponse & LPRVehicleListResult;

// POST: /lprVehicleLists

export type CreateLPRVehicleListRequestPayload = LPRVehicleListMetaUntaggedForPost;

export type CreateLPRVehicleListResponse = LPRVehicleListMeta;

// GET: /lprVehicleLists:listFields

export type LPRVehicleListsFieldsResponse = {
  /** User friendly Displayable name of the field */
  name?: string;
  /** The actual field */
  key?: string;
  /** Indicates whether the possible values for this field is static or unbounded */
  valueRange?: 'fixed' | 'unlimited';
}[];

// GET: /lprVehicleLists:listFieldValues

export type LPRVehicleListsFieldValuesResponse = {
  accessType?: LPRAccessType[];
  /** List of possible values for keys in user supplied data. */
  userData?: object;
  /** List of actors referenced by all lists collectively */
  actor?: LPRActor[];
  /** List of the distinct security statuses present across all the vehicle lists for this account. */
  securityStatus?: ('hotlist' | 'exempted')[];
};

// GET: /lprVehicleLists/{id}

export type LPRVehicleListResponse = LPRVehicleListMeta;

// PATCH: /lprVehicleLists/{id}

export type UpdateLPRVehicleListRequestPayload = LPRVehicleListMetaUntaggedForPatch;

// GET: /lprVehicleLists/{id}/vehicles

export type LPRVehicleRecordsInListQueryParams = {
  /** Provide the license plate in uppercase to be searched with an exact match */
  plate?: string;
  /** Search based on the access type */
  accessType__in?: LPRAccessType[] | string;
  /** Filters by only those records under a specific type of security status */
  securityStatus__in?: LPRSecurityStatus[] | string;
  /**
   * Allows to filter events based on specific values in user supplied fields.
   *   * This allows searching by user supplied attributes instead of plates, for example, apartment number, organization etc.
   *   * If the user for example wishes to search for `organization` having value `ABC` then the correct way to search is `userData.organization=ABC`. This then needs to be specified directly in the query parameters or in json object as a key value pair.
   *   * The list of user Data keys can be obtained by calling `/lprVehicleLists:listFields` endpoint
   * @example {"userData.organization":"ABC","userData.parkingType":"monthly"}
   */
  userData?: Record<string, string>;
} & PaginationQueryParams;

export type LPRVehicleRecordsInListResponse = LPRVehicleListEntriesResult & PaginatedResponse;

// POST: /lprVehicleLists/{id}/vehicles

export type AddNewLPRVehicleRecordInListRequestPayload = LPRVehicleListUntaggedRecord;

export type AddNewLPRVehicleRecordInListResponse = LPRVehicleListTaggedRecord;

// GET: /lprVehicleLists/{id}/vehicles/{recordId}

export type LPRVehicleRecordInListResponse = LPRVehicleListPaddedRecord;

// PATCH: /lprVehicleLists/{id}/vehicles/{recordId}

export type UpdateLPRVehicleRecordInListRequestPayload = LPRVehicleListUntaggedRecordUpdate;

// POST: /lprVehicleLists/{id}/vehicles:bulkCreate

export type LPRVehicleRecordBulkCreateResponse = {
  status: number;
  plate: string;
}[];

// GET: /lprVehicleLists:search

export type LPRVehicleRecordsInAllListsQueryParams = {
  /** Provide the license plate in uppercase to be searched with an exact match */
  plate?: string;
  /**
   * Filter to get only events where the actorType and actorId value equals any one of the supplied value in the list. For each entry of list, the actor type has to be prefixed along with actor id like `actorType:actorId`. For example, to filter for camera with id 100d4c41, the actorId that has to be used is `camera:100d4c41`. To search for events from a specific type of actor, for example users, use a wildcard as actorId: `user:*`.
   * @example ["camera:100d4c41","location:abcd1234"]
   */
  actor?: string[] | string;
  /** Phrase that is used to search for resources (rules or lists) whose names contain it */
  name__contains?: string;
  /** Search based on the access type */
  accessType__in?: LPRAccessType[] | string;
  /** Filters by only those records under a specific type of security status */
  securityStatus__in?: LPRSecurityStatus[] | string;
  /**
   * Allows to filter events based on specific values in user supplied fields.
   *   * This allows searching by user supplied attributes instead of plates, for example, apartment number, organization etc.
   *   * If the user for example wishes to search for `organization` having value `ABC` then the correct way to search is `userData.organization=ABC`. This then needs to be specified directly in the query parameters or in json object as a key value pair.
   *   * The list of user Data keys can be obtained by calling `/lprVehicleLists:listFields` endpoint
   * @example {"userData.organization":"ABC","userData.parkingType":"monthly"}
   */
  userData?: Record<string, string>;
} & PaginationQueryParams;

export type LPRVehicleRecordsInAllListsResponse = PaginatedResponse & LPRVehicleListsSearchResult;

/**
 * LPR Events
 */

// GET: /lprEvents

export type LPREventsQueryParams = {
  /**
   * Provide timestamp to fetch events after this time. Format - 20220626183000.000
   * @format date-time
   */
  timestamp__gte?: string;
  /**
   * Provide timestamp to fetch events before this time. Format - 20220626183000.000
   * @format date-time
   */
  timestamp__lte?: string;
  /** Provide the license plate in uppercase to be searched with an exact match */
  plate?: string;
  /**
   * Fetch events based on the direction of vehicle
   * @example ["entry","exit"]
   */
  direction__in?: ('entry' | 'exit')[] | string;
  /** Search based on the access type */
  accessType__in?: LPRAccessType[] | string;
  /**
   * Filter to get only events where the actorType and actorId value equals any one of the supplied value in the list. For each entry of list, the actor type has to be prefixed along with actor id like `actorType:actorId`. For example, to filter for camera with id 100d4c41, the actorId that has to be used is `camera:100d4c41`. To search for events from a specific type of actor, for example users, use a wildcard as actorId: `user:*`.
   * @example ["camera:100d4c41","location:abcd1234"]
   */
  actor?: string[] | string;
  /** Fetch events based on the color of vehicle */
  color__in?: LPRColorDefinitions[] | string;
  /** Fetch events based on make of vehicle. Examples are "ford", "toyota" etc. */
  make__in?: string[] | string;
  /** Fetch events based on body type of vehicle. Examples are "sedan", "jeep" etc. */
  bodyType__in?: string[] | string;
  /** Filter events based on the type of event. */
  type__in?: ('een.lprPlateReadEvent.v1' | 'een.fleetCodeRecognitionEvent.v1')[] | string;
  /**
   * Allows to filter events based on specific values in user supplied fields.
   *   * This allows searching by user supplied attributes instead of plates, for example, apartment number, organization etc.
   *   * If the user for example wishes to search for `organization` having value `ABC` then the correct way to search is `data.een.userData.v1.organization=ABC`. This then needs to be specified directly in the query parameters or in json object as a key value pair.
   *   * The list of user Data keys can be obtained by calling `/lprVehicleLists:listFields` endpoint
   * @example {"data.een.userData.v1.organization":"ABC","data.een.userData.v1.parkingType":"monthly"}
   */
  userData?: Record<string, string>;
  /** Provide fields to be returned in response body */
  include?:
    | (
        | 'data.een.objectDetection.v1'
        | 'data.een.lprDetection.v1'
        | 'data.een.fullFrameImageUrl.v1'
        | 'data.een.croppedFrameImageUrl.v1'
        | 'data.een.vehicleAttributes.v1'
        | 'data.een.lprAccessType.v1'
        | 'data.een.userData.v1'
        | 'data.een.creatorDetails.v1'
        | 'data.een.dotNumberRecognition.v1'
        | 'data.een.truckNumberRecognition.v1'
        | 'data.een.trailerNumberRecognition.v1'
        | 'data.een.recognizedText.v1'
        | 'data.een.vehicleListInfo.v1'
        | 'data.een.resourceDetails.v1'
        | 'data.een.vspInsightsSummary.v1'
        | 'relatedEvents'
        | 'relatedEvents.event'
      )[]
    | string;
} & PaginationQueryParams;

export type LPREventsResponse = PaginatedResponse & LPRSearchResult;

// GET: /lprEvents/{id}

export type LPREventQueryParams = {
  /** Provide fields to be returned in response body */
  include?:
    | (
        | 'data.een.ObjectDetection.v1'
        | 'data.een.lprDetection.v1'
        | 'data.een.display.v1'
        | 'data.een.lowResKeyImageUrl.v1'
        | 'data.een.maxResKeyImageUrl.v1'
        | 'data.een.croppedImageUrl.v1'
        | 'data.een.vehicleAttributes.v1'
        | 'data.een.accessType.v1'
        | 'data.een.userData.v1'
      )[]
    | string;
};

export type LPREventResponse = LPREventV1;

// GET: /lprEvents:summary

export type LPREventsSummaryQueryParams = {
  /**
   * The caller can also send the userTime along with timezone to the API. This will be used to calculate the current_day count (as per user local time from 00:00 midnight). Time in ISO-8601 format
   * @format date-time
   */
  userTime?: string;
  /** Provide fields to be returned in response body */
  include?: ('today' | 'yesterday' | 'lastHour' | 'last7Days' | 'last30Days')[] | string;
  /** If defined with `actorId` as value, the values are grouped per actor. If not defined, the counts will be aggregated to a single value for all actors. */
  groupBy?: 'actorId' | string;
} & PaginationQueryParams;

export type LPREventsSummaryResponse = LPRSummaryData & PaginatedResponse;

// GET: /lprEvents:listFieldValues

export type LPREventsFieldValuesResponse = LPRFieldsData;

/**
 * LPR Rules
 */

// POST: /lprAlertConditionRules

export type CreateLPRAlertConditionRuleRequestPayload = LPRAlertConditionRuleUntaggedForPost;

export type CreateLPRAlertConditionRuleResponse = LPRAlertConditionRule;

// GET: /lprAlertConditionRules

export type LPRAlertConditionRulesListQueryParams = {
  /**
   * Filter to get only events where the actorType and actorId value equals any one of the supplied value in the list. For each entry of list, the actor type has to be prefixed along with actor id like `actorType:actorId`. For example, to filter for camera with id 100d4c41, the actorId that has to be used is `camera:100d4c41`. To search for events from a specific type of actor, for example users, use a wildcard as actorId: `user:*`.
   * @example ["camera:100d4c41","location:abcd1234"]
   */
  actor?: string[] | string;
  /** Phrase that is used to search for resources (rules or lists) whose names contain it */
  name__contains?: string;
  /** Search based on the rule type */
  type__in?: LPRRuleType | string;
  /** Filter against enabled rules (when set to true) or disabled rules (when set to false) */
  enabled?: boolean;
  /** Search for rules with priority less than or equal to the given value */
  priority__lte?: LPRPriority;
  /** Search for rules with priority greater than or equal to the given value */
  priority__gte?: LPRPriority;
} & PaginationQueryParams;

export type LPRAlertConditionRulesListResponse = LPRRulesResult & PaginatedResponse;

// GET: /lprAlertConditionRules:listFieldValues

export type LPRAlertConditionRulesListFieldValuesResponse = LPRRulesFields;

// GET: /lprAlertConditionRules/{id}

export type LPRAlertConditionRuleResponse = LPRAlertConditionRule;

// PATCH: /lprAlertConditionRules/{id}

export type UpdateLPRAlertConditionRuleRequestPayload = LPRAlertConditionRuleUntaggedForPatch;
